/**
Project: Voice Panel (c)
Title: Routing Policy List 
Description: Component to select and disable the policies.
Copyrights: This file is subject to the terms and conditions defined in file 'LICENSE.txt', which is part of this source code package.
*/
import React, { Component } from "react";
import { connect } from "react-redux";
import {
  Flex,
  Text,
  Dropdown,
  AcceptIcon,
  CloseIcon,
  Dialog,
  Button,
  Divider,
  ChevronEndMediumIcon,
} from "@fluentui/react-northstar";
import { Grid, Row, Col } from "react-flexbox-grid";
import IntlUtil from "../../../../core/helpers/intl-util";
import { VoiceAdminURLProps } from "../../common/settings/voice-admin-urls";
import _ from "lodash";
import TokenUtil from "../../../../core/helpers/token-util";
import { VoiceAdminConstants } from "../../common/settings/voice-admin-constants";

class RoutingPolicyList extends Component {
  _intl_ns_vpa_routing = "vpa_routing";

  constructor(props) {
    super(props);
    this.state = {
      tokenData: TokenUtil.getIdentitySippioAccessTokenData(),
    };
  }

  renderPolicyList = () => {
    if (this.props.routingPolicies && this.props.routingPolicies.length > 0) {
      let policies = [];
      this.props.routingPolicies.forEach((policy, index) => {
        let iconObj = null;
        if (policy.policyStatus === 1) {
          iconObj = (
            <AcceptIcon className="vpa-routing-policy-list-icon-active" />
          );
        } else {
          iconObj = (
            <CloseIcon className="vpa-routing-policy-list-icon-inactive" />
          );
        }
        const item = {
          header: policy.policyId,
          content: (
            <Flex key={`policy-${index}`} className="vpa-routing-policy-list">
              <Text className="vpa-routing-policy-list-text">
                {policy.policyId}
              </Text>
              <Flex className="vpa-routing-policy-list-icon">{iconObj}</Flex>
            </Flex>
          ),
          displayOrder: policy?.displayOrder,
        };
        policies.push(item);
      });
      if (policies?.length > 0)
        policies = _.orderBy(policies, "displayOrder", ["asc"]);
      return (
        <Dropdown
          fluid={true}
          className="vpa-routing-policy-dropdown"
          items={policies}
          placeholder={IntlUtil.getText(
            this._intl_ns_vpa_routing,
            "content.selectRoutingPolicy"
          )}
          renderItem={(Item, props) => (
            <Item {...props} header={``} content={props.content} />
          )}
          defaultValue={this.props.selectedRoutingPolicy.policyId}
          value={this.props.selectedRoutingPolicy.policyId}
          getA11ySelectionMessage={{
            onAdd: (item) => {
              this.props.changePolicy(item.header);
            },
          }}
          inverted={true}
        />
      );
    }
  };

  renderPolicyUpdateButton = () => {
    if (this.props.routingPolicies && this.props.routingPolicies.length > 0) {
      let disablePolicyCount = 0;
      this.props.routingPolicies.forEach((policy) => {
        if (
          policy.policyId !== this.props.selectedRoutingPolicy.policyId &&
          policy.policyStatus === 0
        ) {
          disablePolicyCount++;
        }
      });

      let policyDisableStatus = false;
      if (this.props.routingPolicies.length - disablePolicyCount <= 1)
        policyDisableStatus = true;

      if (this.props.selectedRoutingPolicy.policyStatus === 1) {
        return (
          <Dialog
            backdrop={true}
            cancelButton={IntlUtil.getText(
              this._intl_ns_vpa_routing,
              "content.no"
            )}
            confirmButton={IntlUtil.getText(
              this._intl_ns_vpa_routing,
              "content.yes"
            )}
            onConfirm={() => {
              this.props.disablePolicy(this.props.selectedRoutingPolicy);
            }}
            content={
              <Text className="vpa-page-dialog-info-text">
                {this.getPolicyNotificationText(
                  "notification.warn.disablePolicy",
                  this.props.selectedRoutingPolicy.policyId
                )}
              </Text>
            }
            trigger={
              <Button
                content={IntlUtil.getText(
                  this._intl_ns_vpa_routing,
                  "content.disable"
                )}
                className="vpa-routing-policy-button-disable"
                disabled={policyDisableStatus}
              />
            }
            className="vpa-page-dialog"
          />
        );
      } else {
        return (
          <Dialog
            backdrop={true}
            cancelButton={
              this.state.tokenData?.userRole?.toLowerCase() !==
              VoiceAdminConstants.USER_ROLE.GLOBAL_ADMIN?.toLowerCase()
                ? IntlUtil.getText(this._intl_ns_vpa_routing, "content.close")
                : IntlUtil.getText(this._intl_ns_vpa_routing, "content.no")
            }
            confirmButton={
              this.state.tokenData?.userRole?.toLowerCase() !==
              VoiceAdminConstants.USER_ROLE.GLOBAL_ADMIN?.toLowerCase()
                ? undefined
                : IntlUtil.getText(this._intl_ns_vpa_routing, "content.yes")
            }
            onConfirm={() => {
              this.props.enablePolicy(this.props.selectedRoutingPolicy);
            }}
            content={
              this.state.tokenData?.userRole?.toLowerCase() !==
              VoiceAdminConstants.USER_ROLE.GLOBAL_ADMIN?.toLowerCase() ? (
                <Text className="vpa-page-dialog-info-text">
                  {IntlUtil.getText(
                    this._intl_ns_vpa_routing,
                    "notification.error.noPermission"
                  )}
                </Text>
              ) : (
                <Text className="vpa-page-dialog-info-text">
                  {this.getPolicyNotificationText(
                    "notification.warn.enablePolicy",
                    this.props.selectedRoutingPolicy.policyId
                  )}
                </Text>
              )
            }
            trigger={
              <Button
                content={IntlUtil.getText(
                  this._intl_ns_vpa_routing,
                  "content.enable"
                )}
                className="vpa-routing-policy-button-enable"
              />
            }
            className="vpa-page-dialog"
          />
        );
      }
    }
  };

  showPolicyUsers = () => {
    if (this.props.selectedRoutingPolicy) {
      this.props.history.push({
        pathname: VoiceAdminURLProps.usersList,
        state: {
          name: null,
          email: null,
          phone: null,
          policyId: _.trim(this.props.selectedRoutingPolicy.policyId),
          filterType: null,
        },
      });
    }
  };

  getPolicyNotificationText(keyId, policy) {
    if (keyId && policy) {
      return IntlUtil.getSubstituteText(this._intl_ns_vpa_routing, keyId, [
        {
          key: "<POLICY>",
          value: policy,
        },
      ]);
    } else return null;
  }

  render() {
    return (
      <Flex
        column
        gap="gap.small"
        className="bg-gray m-t-15 p-l-15 p-r-15 p-t-10 p-b-10"
      >
        <Grid fluid className="page-grid">
          <Row>
            <Col xs={12} className="m-b-10">
              <Text className="page-text text-semibold">
                {IntlUtil.getText(
                  this._intl_ns_vpa_routing,
                  "content.selectRoutingPolicy"
                )}
              </Text>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={12} md={7} lg={8} className="m-b-15">
              {this.renderPolicyList()}
            </Col>
            <Col xs={12} sm={12} md={5} lg={4} className="m-b-15">
              {this.renderPolicyUpdateButton()}
            </Col>
          </Row>
          {this.props.selectedRoutingPolicy.policyStatus === 1 ? (
            <>
              <Row>
                <Col xs={12} className="p-b-5">
                  <Divider size={1} className="vpa-routing-divider" />
                </Col>
              </Row>
              <Row>
                <Col xs={12} className="m-b-0 p-l-5">
                  <Button
                    text
                    content={this.getPolicyNotificationText(
                      "content.viewUsersOfPolicy",
                      this.props.selectedRoutingPolicy.policyId
                    )}
                    primary
                    icon={<ChevronEndMediumIcon size="small" />}
                    onClick={() => {
                      this.showPolicyUsers(this.props.selectedRoutingPolicy);
                    }}
                    className="p-l-0"
                  />
                </Col>
              </Row>
            </>
          ) : null}
        </Grid>
      </Flex>
    );
  }
}
const mapStateToProps = (state) => ({
  selectedRoutingPolicy: state.routingStore.selectedRoutingPolicy,
  routingPolicies: state.routingStore.routingPolicies,
});

const mapActionToProps = {};

export default connect(mapStateToProps, mapActionToProps)(RoutingPolicyList);
